import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import api from '../services/api';

/**
 * Campo de seleção de paciente que busca no servidor conforme o usuário digita.
 *
 * Substitui o padrão que havia em onze telas: `api.get('/pacientes')` no mount,
 * guardando a lista inteira em estado para alimentar um `Autocomplete`. Aquela
 * resposta traz CPF, RG, e-mail, telefone, contato de emergência e endereço de
 * cada paciente — dado pessoal da base toda indo para o navegador para exibir
 * nome. Aqui a rota é `/pacientes/busca`, que devolve só id e nome.
 *
 * O valor trafega como objeto `{ id_paciente, nomeCompleto }` para o componente
 * ser troca direta pelos `Autocomplete` que já existiam.
 *
 * @param value        paciente escolhido, ou null
 * @param onChange     recebe o paciente escolhido (ou null ao limpar)
 * @param label        rótulo do campo
 * @param disabled     desabilita o campo
 * @param size         'small' (padrão) ou 'medium'
 * @param error        marca o campo como inválido
 * @param helperText   texto auxiliar sob o campo
 * @param destacado    pinta a borda com a cor da marca quando preenchido, como
 *                     as telas já faziam para sinalizar campo satisfeito
 * @param textFieldProps repassado ao TextField interno
 */
export default function BuscaPaciente({
    value = null,
    onChange,
    label = 'Paciente',
    disabled = false,
    size = 'small',
    error = false,
    helperText,
    destacado = false,
    textFieldProps = {},
}) {
    const [termo, setTermo] = useState('');
    const [opcoes, setOpcoes] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [aberto, setAberto] = useState(false);

    // Guarda a requisição mais recente: respostas fora de ordem não devem
    // sobrescrever o resultado do termo que o usuário está vendo.
    const requisicaoAtual = useRef(0);

    useEffect(() => {
        // Só busca com o campo aberto — evita uma requisição por campo de
        // paciente montado numa tela que tem vários.
        if (!aberto) return;

        const id = ++requisicaoAtual.current;
        const atraso = setTimeout(async () => {
            setCarregando(true);
            try {
                const res = await api.get('/pacientes/busca', {
                    params: { q: termo || undefined, limit: 20 },
                    skipGlobalError: true,
                });
                if (id === requisicaoAtual.current) {
                    setOpcoes(Array.isArray(res.data) ? res.data : []);
                }
            } catch {
                if (id === requisicaoAtual.current) setOpcoes([]);
            } finally {
                if (id === requisicaoAtual.current) setCarregando(false);
            }
        }, termo ? 300 : 0);

        return () => clearTimeout(atraso);
    }, [termo, aberto]);

    // O paciente já escolhido precisa estar entre as opções, senão o MUI
    // reclama de valor fora da lista ao reabrir o campo.
    const opcoesComValor = useMemo(() => {
        if (!value) return opcoes;
        return opcoes.some(o => o.id_paciente === value.id_paciente)
            ? opcoes
            : [value, ...opcoes];
    }, [opcoes, value]);

    return (
        <Autocomplete
            options={opcoesComValor}
            value={value}
            onChange={(_, val) => onChange?.(val)}
            onOpen={() => setAberto(true)}
            onClose={() => setAberto(false)}
            inputValue={undefined}
            onInputChange={(_, texto, motivo) => {
                if (motivo === 'input') setTermo(texto);
                if (motivo === 'clear') setTermo('');
            }}
            getOptionLabel={(o) => o?.nomeCompleto || ''}
            isOptionEqualToValue={(o, v) => o.id_paciente === v.id_paciente}
            filterOptions={(x) => x}   /* o servidor já filtrou */
            loading={carregando}
            loadingText="Buscando..."
            noOptionsText={termo ? 'Nenhum paciente encontrado' : 'Digite para buscar'}
            disabled={disabled}
            fullWidth
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    label={label}
                    size={size}
                    error={error}
                    helperText={helperText}
                    sx={{
                        ...(destacado && value
                            ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1ABB9C' } } }
                            : {}),
                        ...(textFieldProps.sx || {}),
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {carregando ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
